import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Menu, Phone, Mail, MapPin, Instagram, Facebook, Send, Compass } from "lucide-react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/domestic", label: "Domestic" },
  { to: "/international", label: "International" },
  { to: "/group", label: "Group Tours" },
  { to: "/contact", label: "Contact" },
];

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-3 group" data-testid="navbar-logo-link">
      <div className="relative">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#1E3A8A] via-[#1D4ED8] to-[#0EA5E9] flex items-center justify-center shadow-lg shadow-blue-600/30 group-hover:shadow-blue-600/50 transition-shadow">
          <Compass className="w-5 h-5 text-white" strokeWidth={2.5} />
        </div>
        <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-[#1D4ED8] to-[#0EA5E9] opacity-25 blur-md -z-10" />
      </div>
      <div className="leading-tight">
        <div className="font-display font-bold text-[#0A1A3A] text-xl tracking-tight">Happy Journey 4 U</div>
        <div className="text-[10px] font-serif-italic text-slate-500 -mt-0.5 tracking-wider">by Lucky Jeswani</div>
      </div>
    </Link>
  );
}

export default function PublicLayout() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen aurora-bg relative overflow-x-hidden">
      {/* Decorative orbs */}
      <div className="orb orb-blue" style={{ width: 420, height: 420, top: -80, left: -100 }} />
      <div className="orb orb-indigo" style={{ width: 380, height: 380, top: 200, right: -120 }} />

      <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? "py-2" : "py-3"}`} data-testid="public-navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`glass-nav rounded-2xl px-5 lg:px-7 h-16 flex items-center justify-between transition-all ${scrolled ? "h-14" : ""}`}>
            <Logo />

            <nav className="hidden lg:flex items-center gap-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={`nav-link-${n.label.toLowerCase().replace(/ /g, "-")}`}
                  className={({ isActive }) =>
                    `nav-link px-3 py-2 text-[13px] font-medium tracking-wide transition-colors ${
                      isActive ? "text-[#1D4ED8] active" : "text-slate-700 hover:text-[#1D4ED8]"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              <Link to="/contact" className="hidden md:inline-flex">
                <Button className="btn-luxe rounded-xl text-white px-5 h-10" data-testid="navbar-cta-inquire">
                  <Send className="w-3.5 h-3.5 mr-2" /> Plan Trip
                </Button>
              </Link>
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-toggle">
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-72 glass">
                  <div className="flex flex-col gap-2 mt-10">
                    {NAV.map((n) => (
                      <NavLink
                        key={n.to}
                        to={n.to}
                        end={n.to === "/"}
                        onClick={() => setOpen(false)}
                        className={({ isActive }) =>
                          `px-4 py-3 rounded-xl text-sm font-medium ${
                            isActive ? "text-[#1D4ED8] bg-blue-50/80" : "text-slate-700 hover:bg-blue-50/60"
                          }`
                        }
                      >
                        {n.label}
                      </NavLink>
                    ))}
                    <Link to="/contact" onClick={() => setOpen(false)}>
                      <Button className="w-full mt-3 btn-luxe rounded-xl">Plan Trip</Button>
                    </Link>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      <main key={loc.pathname} className="relative z-10">
        <Outlet />
      </main>

      <footer className="relative mt-32 z-10">
        {/* Newsletter band */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <div className="glass-strong p-10 lg:p-14 grid lg:grid-cols-2 gap-8 items-center relative overflow-hidden">
            <div className="orb orb-cyan" style={{ width: 260, height: 260, top: -40, right: -40 }} />
            <div className="relative z-10">
              <div className="text-[10px] font-semibold tracking-[0.3em] text-[#1D4ED8] mb-3">— STAY INSPIRED</div>
              <h3 className="font-display font-bold text-3xl lg:text-4xl text-[#0A1A3A]">Wander through our travel journal</h3>
              <p className="mt-3 text-slate-600 font-serif-italic text-lg">Hand-picked itineraries, hidden gems & seasonal offers — straight to your inbox.</p>
            </div>
            <div className="relative z-10">
              <div className="flex flex-col sm:flex-row gap-2 glass p-2">
                <input type="email" placeholder="your@email.com" className="flex-1 bg-transparent px-4 py-2 sm:py-0 outline-none text-[#0A1A3A] placeholder:text-slate-400" />
                <Button className="btn-luxe rounded-xl text-white w-full sm:w-auto">Subscribe</Button>
              </div>
              <div className="text-xs text-slate-500 mt-2 font-serif-italic">No spam, ever. Unsubscribe anytime.</div>
            </div>
          </div>
        </div>

        <div className="border-t border-white/40 bg-white/30 backdrop-blur-2xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
            <div>
              <Logo />
              <p className="text-sm text-slate-600 mt-4 font-serif-italic leading-relaxed">
                Premium domestic & international tour packages, curated with care for over a decade.
              </p>
              <div className="flex gap-3 mt-5">
                <a href="#" aria-label="Instagram" className="w-10 h-10 glass rounded-xl flex items-center justify-center text-[#1D4ED8] hover:scale-105 transition-transform">
                  <Instagram className="w-4 h-4" />
                </a>
                <a href="#" aria-label="Facebook" className="w-10 h-10 glass rounded-xl flex items-center justify-center text-[#1D4ED8] hover:scale-105 transition-transform">
                  <Facebook className="w-4 h-4" />
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-display font-semibold text-[#0A1A3A] mb-4 text-lg">Tours</h4>
              <ul className="space-y-3 text-sm text-slate-600">
                <li><Link to="/domestic" className="hover:text-[#1D4ED8] transition-colors">Domestic</Link></li>
                <li><Link to="/international" className="hover:text-[#1D4ED8] transition-colors">International</Link></li>
                <li><Link to="/group" className="hover:text-[#1D4ED8] transition-colors">Group Departures</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-display font-semibold text-[#0A1A3A] mb-4 text-lg">Company</h4>
              <ul className="space-y-3 text-sm text-slate-600">
                <li><Link to="/about" className="hover:text-[#1D4ED8] transition-colors">About Us</Link></li>
                <li><Link to="/contact" className="hover:text-[#1D4ED8] transition-colors">Contact</Link></li>
                <li><Link to="/admin" className="hover:text-[#1D4ED8] transition-colors">Agent Portal</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-display font-semibold text-[#0A1A3A] mb-4 text-lg">Reach Us</h4>
              <ul className="space-y-3 text-sm text-slate-600">
                <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#1D4ED8]" /> +91 97532 77771, +91 79871 52260</li>
                <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#1D4ED8]" /> support@happyjourney4u.co.in</li>
                <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#1D4ED8]" /> India</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/40 text-center text-xs text-slate-500 py-5">
            © {new Date().getFullYear()} Happy Journey 4 U. Crafted with care.
          </div>
        </div>
      </footer>
    </div>
  );
}
